/**
 * Auth Store — manajemen state autentikasi EKINKINIAN.
 *
 * Menggunakan Svelte 5 runes ($state) di module scope (.svelte.ts)
 * agar state reaktif global. Komponen mengakses via getter `auth`
 * yang mempertahankan reaktivitas.
 *
 * Acuan: docs/02-UseCase.md UC-01, docs/03-Workflow.md WF-01
 * Requirement: FR-AUTH-01..06, FR-AUTH-08
 */
import { supabase } from './supabase';
import type { Session, User } from '@supabase/supabase-js';

// --- Reactive state (module-level, Svelte 5 runes) ---
let _session = $state<Session | null>(null);
let _user = $state<User | null>(null);
let _loading = $state(true);

let _initialized = false;

/**
 * Inisialisasi auth listener. Idempotent — aman dipanggil berkali-kali.
 *
 * 1. Cek session yang sudah dipersist (Supabase menyimpan di localStorage).
 * 2. Subscribe ke onAuthStateChange untuk menangkap login/logout/refresh.
 *
 * Dipanggil dari root +layout.svelte saat mount.
 */
export function initAuth(): void {
  if (_initialized) return;
  _initialized = true;

  // Cek session existing (dipersist oleh Supabase client)
  void supabase.auth.getSession().then(({ data }) => {
    _session = data.session;
    _user = data.session?.user ?? null;
    _loading = false;
  });

  // Listen perubahan state auth (login, logout, token refresh, token expired)
  supabase.auth.onAuthStateChange((_event, newSession) => {
    _session = newSession;
    _user = newSession?.user ?? null;
    _loading = false;
  });
}

/**
 * Akses reaktif ke state auth.
 * Pakai getter agar reaktivitas $state terjaga di konsumen.
 */
export const auth = {
  get session(): Session | null {
    return _session;
  },
  get user(): User | null {
    return _user;
  },
  get loading(): boolean {
    return _loading;
  },
  /** True jika user terautentikasi (FR-AUTH-02). */
  get isAuthenticated(): boolean {
    return _user !== null;
  },
  /** Email user (untuk tampilan di topbar), null jika belum login. */
  get userEmail(): string | null {
    return _user?.email ?? null;
  },
};

/**
 * Login dengan Google OAuth (FR-AUTH-01).
 *
 * Browser akan redirect ke Google consent, lalu kembali ke `redirectTo`
 * (origin app) dengan session. Supabase menangani pertukaran code → JWT.
 *
 * @throws Error jika gagal memulai OAuth flow.
 */
export async function signInWithGoogle(): Promise<void> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin,
    },
  });
  if (error) throw error;
}

/**
 * Logout: hapus session lokal + revoke di server (FR-AUTH-05).
 *
 * Setelah signOut, onAuthStateChange akan memicu route guard
 * mengarahkan user ke halaman login.
 */
export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}
